import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router';
import { z } from 'zod';
import { useMe } from '../api/auth';
import { ApiError } from '../api/client';
import { useEvents } from '../api/events';
import {
  useDisableUser,
  useEnableUser,
  usePatchUser,
  useResetUserPassword,
  useUser,
} from '../api/users';
import { Field } from '../components/Field';

const editSchema = z.object({
  displayName: z.string().min(1, 'Required'),
  email: z.string().email('Enter a valid email'),
  role: z.enum(['owner', 'manager']),
  eventIds: z.array(z.string()),
});

type EditValues = z.infer<typeof editSchema>;

export function UserEdit() {
  const { userId = '' } = useParams();
  const userQuery = useUser(userId);
  const eventsQuery = useEvents();
  const meQuery = useMe();
  const patch = usePatchUser(userId);
  const disable = useDisableUser();
  const enable = useEnableUser();
  const resetPassword = useResetUserPassword();
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [pwResetText, setPwResetText] = useState('');
  const [pwResetMessage, setPwResetMessage] = useState<string | null>(null);

  const form = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: { displayName: '', email: '', role: 'manager', eventIds: [] },
  });

  const role = form.watch('role');

  useEffect(() => {
    if (userQuery.data) {
      form.reset({
        displayName: userQuery.data.displayName,
        email: userQuery.data.email,
        role: userQuery.data.role,
        eventIds: userQuery.data.eventGrants?.map((g) => g.id) ?? [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userQuery.data?.id]);

  useEffect(() => {
    if (savedAt === null) return;
    const t = setTimeout(() => setSavedAt(null), 2500);
    return () => clearTimeout(t);
  }, [savedAt]);

  if (userQuery.isPending || meQuery.isPending) return <div className="loading">Loading…</div>;
  if (!userQuery.data) return <div className="error">Could not load user.</div>;

  const user = userQuery.data;
  const meId = meQuery.data?.id;
  const isSelf = meId === user.id;

  const onSubmit = form.handleSubmit(async (values) => {
    form.clearErrors('root');
    try {
      await patch.mutateAsync({
        displayName: values.displayName,
        email: values.email,
        role: values.role,
        eventIds: values.role === 'manager' ? values.eventIds : [],
      });
      setSavedAt(Date.now());
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'email_taken') {
          form.setError('email', { message: 'Email already in use' });
        } else if (err.code === 'last_owner') {
          form.setError('root', {
            message: "Can't demote the only Owner — promote someone else first.",
          });
        } else {
          form.setError('root', { message: 'Could not save changes.' });
        }
      } else {
        form.setError('root', { message: "Couldn't reach the server." });
      }
    }
  });

  async function handleDisable() {
    try {
      await disable.mutateAsync(user.id);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'last_owner') {
        form.setError('root', { message: "Can't disable the only Owner." });
      }
    }
  }

  async function handleEnable() {
    await enable.mutateAsync(user.id);
  }

  async function handleResetPassword() {
    if (pwResetText.length < 8) {
      setPwResetMessage('At least 8 characters.');
      return;
    }
    try {
      await resetPassword.mutateAsync({ userId: user.id, password: pwResetText });
      setPwResetMessage('Temporary password set. Share it with the user.');
      setPwResetText('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setPwResetMessage('Too many resets. Try again in a minute.');
      } else {
        setPwResetMessage('Could not reset password.');
      }
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Edit user</h1>
        <div className="page-actions">
          <Link to="/users" className="btn btn-ghost">
            Back
          </Link>
        </div>
      </div>

      <form onSubmit={onSubmit} className="card card-pad" style={{ marginBottom: 24 }}>
        <Field label="Display name" error={form.formState.errors.displayName?.message}>
          <input type="text" autoComplete="off" {...form.register('displayName')} />
        </Field>
        <Field label="Email" error={form.formState.errors.email?.message}>
          <input type="email" autoComplete="off" {...form.register('email')} />
        </Field>
        <Field
          label="Role"
          help={
            isSelf
              ? "You can't change your own role. Ask another Owner if you need this."
              : undefined
          }
        >
          <select disabled={isSelf} {...form.register('role')}>
            <option value="manager">Manager</option>
            <option value="owner">Owner</option>
          </select>
        </Field>

        {role === 'manager' && (
          <Field label="Granted events" help="Managers see only the events checked here.">
            <div
              className="user-grants"
              style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}
            >
              {eventsQuery.isPending && <div className="micro">Loading events…</div>}
              {eventsQuery.data?.map((ev) => (
                <label
                  key={ev.id}
                  className="user-grant-row"
                  style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                >
                  <input type="checkbox" value={ev.id} {...form.register('eventIds')} />
                  <span>
                    {ev.name} <span className="micro">({ev.shortCode})</span>
                  </span>
                </label>
              ))}
            </div>
          </Field>
        )}

        {form.formState.errors.root && (
          <div className="form-error">{form.formState.errors.root.message}</div>
        )}

        <div className="page-actions">
          <button type="submit" className="btn btn-primary" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Saving…' : 'Save changes'}
          </button>
          {savedAt !== null && <span className="micro">Saved.</span>}
        </div>
      </form>

      {!isSelf && (
        <div className="card card-pad" style={{ marginBottom: 24 }}>
          <h2>Reset password</h2>
          <p className="micro">
            Sets a new temporary password for this user. Their existing sessions are revoked.
          </p>
          <Field label="New temporary password">
            <input
              type="text"
              value={pwResetText}
              onChange={(e) => setPwResetText(e.target.value)}
              autoComplete="off"
            />
          </Field>
          <div className="page-actions">
            <button type="button" className="btn btn-ghost" onClick={handleResetPassword}>
              Set temporary password
            </button>
            {pwResetMessage && <span className="micro">{pwResetMessage}</span>}
          </div>
        </div>
      )}

      {!isSelf && (
        <div className="card card-pad">
          <h2>{user.disabledAt ? 'Re-enable account' : 'Disable account'}</h2>
          <p className="micro">
            {user.disabledAt
              ? 'Restore login access. The user can sign in with their existing password.'
              : 'Block this user from signing in. Existing sessions are revoked. Reversible.'}
          </p>
          <div className="page-actions">
            {user.disabledAt ? (
              <button type="button" className="btn btn-primary" onClick={handleEnable}>
                Re-enable
              </button>
            ) : (
              <button type="button" className="btn btn-danger" onClick={handleDisable}>
                Disable
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
